import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import CommonModal from '../../Common/Modal/Modal';
import {
  useFetchNote, createNote, updateNote, deleteNote,
} from '../../../utils/api/notes';
import { useDialogs, useUnsavedChangesWarning, useCloseDialogWithUnsavedChanges } from '../../../hooks';
import ViewModal from './ViewModal';
import AddEditModal from './AddEditModal';
import {
  handleLongTitle, borderColor, handleErrorMessage, formatNoteData,
} from '../../../utils/notes';
import { formatVisitAndNoteDate } from '../../../utils/dates';
import { getRoleChecks } from '../../../utils/roles';

const NoteViewAddEditModal = ({
  activeNoteId = undefined, // props for update note
  currentUserId, // prop used for permissions based off the author of the note
  currentUserRole, // prop used for note permissions based off the user's role
  organizationId, // need for API
  onNotesChange = undefined, // called after a note is deleted or updated so the parent can refetch the notes list
  setOpenAddEditModal, // open modal to add note when click 'add note' button and open edit modal from view note modal
  siteId = undefined, // required for creating note
  siteName = undefined, // display site name on add note modal
  isAddMode = undefined, // mode to show add note modal directly with skipping view note modal
  handleChangeNote = undefined, // function to update notes list on Site Detail page after creating note (without fetching all notes)
  setNoteModalsOpen = undefined, // until noteModalIsOpen is true, no fetching notes on NotesPage.js
  showToast,
  setSelectedNote,
  // New props for URL-synced edit mode
  isEditMode = false,
  onOpenEditMode,
  onCloseEditMode,
}) => {
  const [note, setNote] = useState({
    title: '',
    content: '',
    status: 'open', // default status to prefill the dropdown for create note
    priority: 'no_priority', // default priority to prefill the dropdown for create note
    timestamp: formatVisitAndNoteDate(new Date()), // default timestamp prefield with todays date
  });

  // Use URL-synced edit mode if provided, otherwise fallback to internal state
  const [internalAddEditMode, setInternalAddEditMode] = useState(isAddMode || false);
  const addEditMode = onOpenEditMode && onCloseEditMode ? isEditMode : internalAddEditMode;

  const [updatedNote, setUpdatedNote] = useState(false);
  const [errorMessage, setErrorMessage] = useState({
    date: '', title: '',
  });
  const [disableSubmit, setDisableSubmit] = useState(false); // disable update/create button until user starts typing and when the API request is in progress
  const { openDialog, closeDialog } = useDialogs();
  const fetchNote = useFetchNote(activeNoteId, organizationId, setNote, showToast);
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);
  const { isManager, isPlanner } = getRoleChecks(currentUserRole);

  useUnsavedChangesWarning(hasUnsavedChanges);
  useEffect(() => {
    if (!activeNoteId) return;
    fetchNote();
  }, [fetchNote, activeNoteId]);

  const modalClassName = `border-t-12px ${borderColor(note)}`;

  const handleCreateUpdateNote = (orgId, data, id) => {
    // create error message if date or title is empty
    const errors = handleErrorMessage(data);
    // to prevent sending request if there are empty input fields in the form
    if (Object.keys(errors).length > 0) {
      setErrorMessage({ ...errorMessage, ...errors });
      return;
    }
    const apiRequest = note?.id ? updateNote : createNote;
    // format data for create/update note
    const formattedData = formatNoteData(data, isAddMode, siteId, currentUserId);
    setDisableSubmit(true); // disable create/update button until API request is in progress
    apiRequest(orgId, formattedData, id) // attention! id is undefined when creating a new note, so send it as the last param.
      .then(response => {
        if (!response.ok) {
          throw new Error(`Failed to ${note?.id ? 'update' : 'create'} note`);
        }
        return response.json();
      })
      .then(d => {
        if (!d.note) {
          throw new Error(d.error);
        }
        // create custom message for toast
        showToast({ message: `"${handleLongTitle(d.note.title)}" has been ${note?.id ? 'updated' : 'created'}.`, type: 'success' });
        // update notes list on Site Detail page after creating note (without fetching all notes)
        if (handleChangeNote) {
          handleChangeNote(d.note, setOpenAddEditModal);
        }
        // prevent rerendering notes on NoteCards.js and NotesTable.js when view modal is open
        if (setNoteModalsOpen) {
          setNoteModalsOpen(true);
        }
        setNote(d.note); // update note state from successful response to show updated data in the view modal
        setUpdatedNote(true); // rerender notes on NoteCards.js and NotesTable.js when view modal is closed

        // Close edit mode using appropriate method
        if (onCloseEditMode) {
          onCloseEditMode(); // URL-synced edit mode
        } else {
          setInternalAddEditMode(false); // Internal edit mode
        }

        setErrorMessage({ date: '', title: '' });
        if (setSelectedNote) {
          setSelectedNote(null);
        }
      })
      .catch((error) => {
        showToast({ message: error.message, type: 'error' });
      })
      .finally(() => {
        setDisableSubmit(false);
      });
  };

  const handleDeleteNote = (id, orgId) => {
    deleteNote(id, orgId)
      .then((response) => {
        if (!response.ok) {
          throw new Error('Failed to delete note');
        }
        showToast({ message: `"${handleLongTitle(note.title)}" has been deleted.`, type: 'success' });
        if (handleChangeNote) {
          handleChangeNote({ id: note.id, deleted: true });
        }
        setOpenAddEditModal(false); // close add/edit modal
        if (onNotesChange) {
          onNotesChange();
        }
        if (setSelectedNote) {
          setSelectedNote(null);
        }
      })
      .catch((error) => {
        showToast({ message: error.message, type: 'error' });
      });
  };

  const handleOpenDeleteModal = () => {
    openDialog({
      message: [`Delete "${handleLongTitle(note.title)}"?`],
      show: true,
      actions: [
        {
          label: 'Cancel',
          onClick: () => {
            closeDialog();
            if (setNoteModalsOpen) {
              setNoteModalsOpen(false);
            }
          },
          variant: 'secondary',
        },
        {
          label: 'Delete',
          dataTestId: 'note-confirm-delete-button',
          onClick: () => {
            handleDeleteNote(note.id, organizationId);
            setOpenAddEditModal(false);
            closeDialog();
          },
          variant: 'primary',
        },
      ],
    });
  };

  const canEdit = isManager || (isPlanner && note.user_id === currentUserId);

  const actionViewModalButtons = [
    ...(isManager
      ? [
        {
          label: 'Delete',
          dataTestId: 'note-delete-button',
          variant: 'secondary-delete',
          onClick: () => {
            handleOpenDeleteModal(note.id);
          },
        },
      ]
      : []),

    ...(canEdit
      ? [
        {
          label: 'Edit',
          variant: 'primary',
          onClick: () => {
            if (onOpenEditMode) {
              onOpenEditMode();
            } else {
              setInternalAddEditMode(true);
            }
          },
        },
      ]
      : []),
  ];

  const closeAddNoteModal = () => {
    setOpenAddEditModal(false);
    if (onCloseEditMode) {
      onCloseEditMode();
    } else {
      setInternalAddEditMode(false);
    }
  };

  const closeEditModal = () => {
    if (onCloseEditMode) {
      onCloseEditMode(); // URL-synced edit mode
    } else {
      setInternalAddEditMode(false); // Internal edit mode
    }
  };

  const confirmClose = isAddMode ? closeAddNoteModal : closeEditModal;

  const handleCloseDialog = useCloseDialogWithUnsavedChanges({
    isUnsavedChanges: hasUnsavedChanges,
    setIsUnsavedChanges: setHasUnsavedChanges,
    redirectCallback: () => confirmClose(),
  });

  const closeAddEditModal = () => {
    handleCloseDialog();
  };

  const actionEditModalButtons = [
    {
      label: 'Cancel',
      variant: 'secondary',
      onClick: () => {
        closeAddEditModal();
      },
    },
    {
      label: note.id ? 'Update' : 'Create',
      variant: 'primary',
      type: 'submit',
      isDisabled: disableSubmit,
      onClick: (formData) => {
        handleCreateUpdateNote(organizationId, formData, note?.id);
      },
    },
  ];

  const closeModal = () => {
    setOpenAddEditModal(false);
    if (setNoteModalsOpen) {
      setNoteModalsOpen(false);
    }
    if (updatedNote && onNotesChange) {
      onNotesChange();
    }
    setUpdatedNote(false);
    if (setSelectedNote) {
      setSelectedNote(null);
    }
  };

  const renderModalContent = () => {
    if (addEditMode) {
      return (
        <AddEditModal
          note={note}
          actionItemButtons={actionEditModalButtons}
          siteName={siteName}
          errorMessage={errorMessage}
          setDisableSubmit={setDisableSubmit}
          setIsUnsavedChanges={setHasUnsavedChanges}
        />
      );
    }
    if (!addEditMode && !isAddMode) {
      return (
        <ViewModal
          note={note}
          actionItemButtons={actionViewModalButtons}
        />
      );
    }
    return null;
  };

  const handleShowModal = () => {
    const modalContent = renderModalContent();
    if (!modalContent) return null;

    return (
      <CommonModal
        boxClassName="w-4/5 md:w-3/5 lg:max-w-screen-sm"
        title={isAddMode ? 'Create Note' : note.title}
        // if it is a view mode - just close the modal, if it is an add/edit mode - show dialog to confirm that user want to close the modal with unsaved changes
        handleClose={isAddMode || addEditMode ? closeAddEditModal : closeModal}
        shadow="shadow-vamos-drop-shadow"
        textAlignment="text-left"
        titlePosition="left"
        closeIconPosition=""
        headerClassName="p-5.5"
        className={addEditMode ? '' : modalClassName}
      >
        {modalContent}
      </CommonModal>
    );
  };

  return (
    <>
      {handleShowModal()}
    </>
  );
};

NoteViewAddEditModal.propTypes = {
  activeNoteId: PropTypes.number,
  currentUserId: PropTypes.number.isRequired,
  currentUserRole: PropTypes.string.isRequired,
  organizationId: PropTypes.number.isRequired,
  setNoteModalsOpen: PropTypes.func,
  setOpenAddEditModal: PropTypes.func.isRequired,
  siteId: PropTypes.number,
  siteName: PropTypes.string,
  isAddMode: PropTypes.bool,
  handleChangeNote: PropTypes.func,
  showToast: PropTypes.func.isRequired,
  onNotesChange: PropTypes.func,
  setSelectedNote: PropTypes.func,
  // New props for URL-synced edit mode
  isEditMode: PropTypes.bool,
  onOpenEditMode: PropTypes.func,
  onCloseEditMode: PropTypes.func,
};

export default NoteViewAddEditModal;
